import {
  Box,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuthState } from 'state/useAuthState';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { Fragment, useContext } from 'react';
import { useTabs } from 'hooks/useTabs';
import { InlineStatus } from 'components/InlineStatus';
import { useLockMapPinActions } from 'features/mapping/mapPin';
import { BroadcastifyFeeds } from 'features/broadcastify/components/BroadcastifyFeeds';
import {
  useLatLngMetadata,
  getSlackChannelsFromMetadata,
} from 'hooks/useLatLngMetadata';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { PlaceAddress, Place } from 'features/places/places.types';
import { getLatLngLocationName } from 'shared/utils';
import { buildLocationState, useCurrentLocation } from 'hooks/useLocationState';
import { Link } from 'react-router-dom';
import { usePlaces } from 'features/places/hooks/usePlaces';
import { useColorScheme } from 'hooks/useColorScheme';
import { MapEntityDrawerHeader } from '../MapEntityDrawerHeader';
import { MapEntityDrawerContext } from '../MapEntityDrawer';
import { MapPinInfo } from './MapPinInfo';
import { MapPinInternalInfo } from './MapPinInternalInfo';
import {
  formatAddressMultiline,
  getMapPinHeaderTitle,
} from './MapPinDrawerContent.utils';
import { UserStatusChip } from '../../../../components/UserStatusChip';

const TAB_PANEL_ID_PREFIX = 'map-pin-details';

type MapPinDrawerContentProps = {
  lat: number;
  lng: number;
  place?: Place;
};

export const MapPinDrawerContent = ({
  lat,
  lng,
  place,
}: MapPinDrawerContentProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    showMembershipProFeatures,
    permissions: { isInternalUser },
  } = useAuthState();

  const pinImprovements = useReleaseFlag('pin_drop_improvements');
  const { isDarkMode } = useColorScheme();
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const { isLocked, lockPin, unlockPin } = useLockMapPinActions();
  const { latLngMetadata, isLatLngMetadataLoading } = useLatLngMetadata({
    enabled: pinImprovements
      ? Boolean(lat) && Boolean(lng)
      : showMembershipProFeatures && Boolean(lat) && Boolean(lng),
    lat,
    lng,
  });

  const countyName = latLngMetadata?.region?.displayName ?? '...';

  const headerTitle = getMapPinHeaderTitle({
    pinImprovements,
    place,
    address: latLngMetadata?.address,
    addressLabel: t('mapPin.info.address'),
    countyName,
  });

  const { canAddPlace } = usePlaces();

  const fromRoute = useCurrentLocation();

  const address = latLngMetadata?.address;

  const savePlaceState = buildLocationState<PlaceAddress>(fromRoute, {
    name: address ?? getLatLngLocationName(lat, lng),
    formattedAddress: address ?? '',
    coordinates: { latitude: lat, longitude: lng },
    type: address ? 'Feature' : 'LatLngLocation',
  });

  const slackChannels = getSlackChannelsFromMetadata(latLngMetadata);

  const tabData = [
    {
      tabId: 'info',
      label: t('mapPin.tabs.info'),
      enabled: true,
      content: (
        <MapPinInfo
          coordinates={{ lat, lng }}
          place={place}
          wildfireChannel={slackChannels.wildfireChannel}
          wildfireChannelId={slackChannels.wildfireChannelId}
          floodingChannel={slackChannels.floodingChannel}
          floodingChannelId={slackChannels.floodingChannelId}
        />
      ),
    },
    ...(pinImprovements && isInternalUser && !place
      ? [
          {
            tabId: 'internal',
            label: t('mapPin.tabs.internal'),
            enabled: true,
            content: <MapPinInternalInfo latLngMetadata={latLngMetadata} />,
          },
        ]
      : []),
    ...(!pinImprovements || !place
      ? [
          {
            tabId: pinImprovements ? 'feeds' : 'broadcastify',
            label: pinImprovements
              ? t('feeds.label')
              : t('mapPin.tabs.broadcastify'),
            adornment: pinImprovements ? (
              <UserStatusChip type="pro" size="small" />
            ) : undefined,
            enabled: true,
            content: (
              <BroadcastifyFeeds lat={lat} lng={lng} withHeader={false} />
            ),
          },
        ]
      : []),
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('geoEvent.tabsLabel'),
    tabData,
  });

  if (isLatLngMetadataLoading) {
    return <InlineStatus status="loading" />;
  }

  return (
    <Box>
      <DrawerStickyHeader
        scrollContainer={scrollElementRef?.current as HTMLElement}
      >
        <DrawerStickyHeader.ContentContainer
          sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <MapEntityDrawerHeader>{headerTitle}</MapEntityDrawerHeader>

          {pinImprovements && latLngMetadata?.address && (
            <>
              <Typography component="address">
                {formatAddressMultiline(latLngMetadata.address).map(
                  (line, index) => (
                    <Fragment key={line}>
                      {index > 0 && <br />}
                      {line}
                    </Fragment>
                  ),
                )}
              </Typography>
              <Typography variant="body2" color="gray" sx={{ mb: 1 }}>
                {latLngMetadata.region?.displayName}
              </Typography>
            </>
          )}

          {pinImprovements && !place && (
            <Button
              component={Link}
              to={{ pathname: '/place/create', state: savePlaceState }}
              size="large"
              sx={{ mb: 2 }}
              fullWidth
            >
              <Stack component="span" direction="row" spacing={1}>
                <Box component="span">{t('mapPin.savePlace')}</Box>
                {!canAddPlace && (
                  <UserStatusChip
                    type="members"
                    size="small"
                    color={isDarkMode ? 'black' : undefined}
                  />
                )}
              </Stack>
            </Button>
          )}

          {isInternalUser && !place && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isLocked}
                  onChange={(e): void => {
                    if (e.target.checked) {
                      if (lat && lng) {
                        lockPin({ lat, lng });
                      }
                    } else {
                      unlockPin();
                    }
                  }}
                />
              }
              label={t('mapPin.lockPin')}
            />
          )}
        </DrawerStickyHeader.ContentContainer>

        <Tabs {...tabsProps}>
          {tabProps.map((tProps) => {
            // 'key' prop should not be spread into JSX
            const { key, ...rest } = tProps;
            return <Tab key={key} {...rest} />;
          })}
        </Tabs>
      </DrawerStickyHeader>

      {tabPanels}
    </Box>
  );
};
